import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { updPassword } from '@/controller/auth';
import FormField from '@/shared/components/UI/FormField';
import { toast } from '@/shared/components/UI/Toast';
import { useTranslations } from '@/translations/useTranslations.js';

const ChangePassword = () => {
    const { t } = useTranslations();

    const [formData, setFormData] = useState({
        password: '',
        confirm_password: '',
    });
    const [errors, setErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const [loading, setLoading] = useState(false);

    const nav = useNavigate();

    // Handle form field changes
    const handleFieldChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));

        if (errors[field]) {
            setErrors((prev) => ({
                ...prev,
                [field]: null,
            }));
        }
    };

    // Form validation
    const validateForm = () => {
        const newErrors = {};

        if (!formData.password.trim()) {
            newErrors.password = 'New password is required';
        } else if (formData.password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters';
        }

        if (!formData.confirm_password.trim()) {
            newErrors.confirm_password = 'Confirm password is required';
        } else if (formData.confirm_password.length < 8) {
            newErrors.confirm_password = 'Confirm password must be at least 8 characters';
        } else if (formData.password !== formData.confirm_password) {
            newErrors.confirm_password = 'Passwords do not match';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        await onFinish(formData);
    };

    const onFinish = async (v) => {
        setLoading(true);
        try {
            const ret = await updPassword(v.password, v.confirm_password);
            if (ret.status === 200) {
                toast.success('Password Update Successfully');
                nav('/member/register-product');
            } else {
                toast.error(ret?.data?.error?.message || 'Error updating password');
            }
        } catch (e) {
            toast.error('Something went wrong.');
        }
        setLoading(false);
    };

    return (
        <div className="relative flex size-full min-h-screen flex-col bg-white">
            <div className="layout-container flex h-full grow flex-col">
                <div className="px-4 sm:px-8 md:px-16 lg:px-32 xl:px-40 flex flex-1 justify-center py-5">
                    <div className="layout-content-container flex flex-col max-w-[960px] flex-1">
                        {/* Header Section */}
                        <div className="flex flex-wrap justify-between gap-3 p-4 mb-6">
                            <div>
                                <p className="text-[#111218] tracking-light text-2xl sm:text-3xl lg:text-[32px] font-bold leading-tight m-0">
                                    Change Password
                                </p>
                                <p className="text-[#5f678c] text-sm font-normal leading-normal mt-2">
                                    Update your password to keep your account secure.
                                </p>
                            </div>
                        </div>

                        {/* Loading Overlay */}
                        {loading && (
                            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                                <div className="bg-white rounded-lg p-6 flex items-center space-x-3">
                                    <div className="w-6 h-6 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
                                    <span className="text-gray-700">Updating...</span>
                                </div>
                            </div>
                        )}

                        {/* Change Password Form */}
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="bg-gray-50 rounded-xl p-6 mb-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* New Password Field */}
                                    <FormField
                                        label="New Password"
                                        required={true}
                                        error={errors.password}
                                        className="!max-w-none !px-4 !py-3"
                                    >
                                        <div className="relative">
                                            <input
                                                type={showPassword ? 'text' : 'password'}
                                                className={`w-full h-14 rounded-xl border ${errors.password ? 'border-red-500' : 'border-gray-300'} bg-white px-4 pr-12 text-base outline-none focus:border-blue-500 focus-ring transition-colors`}
                                                value={formData.password}
                                                onChange={(e) => handleFieldChange('password', e.target.value)}
                                                placeholder={t('form.password')}
                                                maxLength={50}
                                                disabled={loading}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors duration-200"
                                            >
                                                {showPassword ? (
                                                    <svg
                                                        className="w-5 h-5"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                                                        />
                                                    </svg>
                                                ) : (
                                                    <svg
                                                        className="w-5 h-5"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                                        />
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                                        />
                                                    </svg>
                                                )}
                                            </button>
                                        </div>
                                    </FormField>
                                    {/* Confirm Password Field */}
                                    <FormField
                                        label={t('form.confirm_password')}
                                        required={true}
                                        error={errors.confirm_password}
                                        className="!max-w-none !px-4 !py-3"
                                    >
                                        <div className="relative">
                                            <input
                                                type={showConfirmPassword ? 'text' : 'password'}
                                                className={`w-full h-14 rounded-xl border ${errors.confirm_password ? 'border-red-500' : 'border-gray-300'} bg-white px-4 pr-12 text-base outline-none focus:border-blue-500 focus-ring transition-colors`}
                                                value={formData.confirm_password}
                                                onChange={(e) => handleFieldChange('confirm_password', e.target.value)}
                                                placeholder={t('form.confirm_password')}
                                                maxLength={50}
                                                disabled={loading}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors duration-200"
                                            >
                                                {showConfirmPassword ? (
                                                    <svg
                                                        className="w-5 h-5"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                                                        />
                                                    </svg>
                                                ) : (
                                                    <svg
                                                        className="w-5 h-5"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                                        />
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                                        />
                                                    </svg>
                                                )}
                                            </button>
                                        </div>
                                    </FormField>
                                </div>
                            </div>
                            {/* Submit Button */}
                            <div className="p-4 flex justify-center">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="flex min-w-[120px] max-w-[300px] cursor-pointer items-center justify-center overflow-hidden rounded-xl h-12 px-6 bg-gray-50 text-[#121317] border border-gray-300 text-sm font-bold leading-normal transition-colors duration-200 hover:bg-gray-200 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed w-full"
                                >
                                    <span className="overflow-hidden">
                                        {loading ? 'Updating...' : t('form.update')}
                                    </span>
                                </button>
                            </div>
                        </form>

                        {/* Bottom spacing for footer */}
                        <div className="pb-24" />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChangePassword;
